import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Search, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const FILTERS = [
  { key: 'all', label: 'All Clients' },
  { key: 'new_plus', label: 'New Plus' },
  { key: 'new_coaching', label: 'New Coaching — Action' },
  { key: 'new_tracker', label: 'New Tracker' },
  { key: 'awaiting_payment', label: 'Awaiting Payment' },
  { key: 'in_programme', label: 'In Programme' },
  { key: 'completed', label: 'Completed' },
];
// Payment → programme pipeline (unpaid clients never receive a programme).
export const PIPELINE = [
  { key: 'unpaid', label: 'Onboarded — Unpaid', color: '#FFB800' },
  { key: 'awaiting_generation', label: 'Paid — Awaiting Generation', color: '#00C8FF' },
  { key: 'awaiting_review', label: "Awaiting Coach JP's Review", color: '#FF8A4C' },
  { key: 'active', label: 'Active Clients', color: '#65A30D' },
];

const PRED = {
  all: () => true,
  new_plus: c => c.programme_type === 'Plus' && c.is_new,
  new_coaching: c => c.action_required === true,
  new_tracker: c => c.programme_status === 'New Tracker',
  awaiting_payment: c => c.programme_status === 'Awaiting Payment',
  in_programme: c => c.programme_status === 'In Programme',
  completed: c => c.programme_status === 'Completed',
  ...Object.fromEntries(PIPELINE.map(p => [`stage_${p.key}`, c => c.pipeline_stage === p.key])),
  // dashboard KPI deep-links:
  active: c => c.status === 'active',
  new: c => c.is_new,
  at_risk: c => (c.regen_score ?? 0) < 40,
};

const PS_STYLE = {
  'In Programme': 'bg-[var(--c-green-soft)] text-[var(--c-green-ink)]',
  'Completed': 'bg-[var(--c-film)] text-muted-foreground',
  'New — Action Required': 'bg-[#FF8A4C]/15 text-[#c2410c]',
  'New — Automated': 'bg-[#00C8FF]/12 text-[#00849c]',
  'New Tracker': 'bg-[#7DD3FC]/18 text-[#0369a1]',
  'Tracker Active': 'bg-[#7DD3FC]/18 text-[#0369a1]',
  'Awaiting Payment': 'bg-[#FFB800]/15 text-[#a16207]',
  'New': 'bg-[var(--c-film)] text-muted-foreground',
  'Needs Review': 'bg-[#FF5C5C]/12 text-[#b91c1c]',
};
const TYPE_STYLE = {
  Plus: 'bg-[#00C8FF]/12 text-[#00849c]',
  Coaching: 'bg-[#A78BFA]/15 text-[#6d28d9]',
  Tracker: 'bg-[#7DD3FC]/18 text-[#0369a1]',
  '—': 'bg-[var(--c-film)] text-muted-foreground',
};

const PSBadge = ({ c }) => (
  <span className={`pill inline-flex items-center gap-1 ${PS_STYLE[c.programme_status] || 'bg-[var(--c-film)] text-muted-foreground'}`} data-testid={`prog-status-${c.id}`}>
    {c.action_required && <AlertTriangle className="w-3 h-3" />}{c.programme_status}
  </span>
);
const TypeBadge = ({ t }) => <span className={`pill ${TYPE_STYLE[t] || TYPE_STYLE['—']}`}>{t}</span>;

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState('');
  const [err, setErr] = useState('');
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const active = PRED[params.get('filter')] ? params.get('filter') : 'all';

  const load = () => {
    setErr('');
    api.get('/clients')
      .then(r => setClients(Array.isArray(r.data) ? r.data : []))
      .catch(e => { setErr(e.response?.data?.detail || 'Failed to load clients'); toast.error('Failed to load clients'); });
  };
  useEffect(() => { load(); }, []);

  const setFilter = (k) => { const p = new URLSearchParams(params); if (k === 'all') p.delete('filter'); else p.set('filter', k); setParams(p); };
  const filtered = clients.filter(PRED[active]).filter(c =>
    c.name?.toLowerCase().includes(q.toLowerCase()) || c.email?.toLowerCase().includes(q.toLowerCase()));

  const handleDelete = async (e, c) => {
    e.stopPropagation();
    if (!confirm(`Delete ${c.name} (${c.email})? This permanently removes the client and all their data.`)) return;
    try { await api.delete(`/clients/${c.id}`); toast.success(`${c.name} deleted`); setClients(cs => cs.filter(x => x.id !== c.id)); }
    catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="space-y-5" data-testid="admin-clients">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="label-mini text-[#00C8FF]">People</div>
          <h1 className="font-display font-black text-3xl lg:text-4xl mt-1">Clients</h1>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search by name or email…" value={q} onChange={e => setQ(e.target.value)} data-testid="client-search" className="pl-9 bg-regen-card border-[var(--c-hair)] w-full sm:w-72" />
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2" data-testid="client-filters">
        {FILTERS.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`filter-${f.key}`}
            className={`pill transition ${active === f.key ? 'bg-[#00C8FF] text-[#081726] font-semibold' : 'bg-[var(--c-film)] text-muted-foreground hover:text-foreground'}`}>
            {f.label}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2" data-testid="client-pipeline-filters">
        {PIPELINE.map(p => {
          const k = `stage_${p.key}`;
          const n = clients.filter(PRED[k]).length;
          return (
            <button key={k} onClick={() => setFilter(k)} data-testid={`filter-${k}`}
              className={`pill transition border ${active === k ? 'font-semibold text-[#081726]' : 'text-muted-foreground hover:text-foreground bg-transparent'}`}
              style={active === k ? { background: p.color, borderColor: p.color } : { borderColor: `${p.color}66` }}>
              {p.label} · {n}
            </button>
          );
        })}
      </div>

      {err && (
        <div data-testid="clients-error" className="regen-card-elevated p-4 border border-[#FF5C5C]/30 text-sm text-[#FF5C5C] flex items-center justify-between gap-3">
          <span>Couldn't load clients: {err}</span>
          <button onClick={load} className="pill bg-[var(--c-film)] text-foreground">Retry</button>
        </div>
      )}

      {/* MOBILE: card layout */}
      <div className="lg:hidden space-y-3">
        {filtered.map(c => (
          <div key={c.id} onClick={() => nav(`/admin/clients/${c.id}`)} data-testid={`client-card-${c.id}`}
            className={`w-full regen-card-elevated p-4 text-left active:scale-[0.99] transition cursor-pointer ${c.action_required ? 'border border-[#FF8A4C]/40' : ''}`}>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#00C8FF] to-[var(--c-green)] flex items-center justify-center text-[#081726] font-bold flex-shrink-0">{(c.name || '?').split(' ').map(p => p[0]).slice(0,2).join('')}</div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold truncate">{c.name}</div>
                <div className="text-xs text-muted-foreground truncate">{c.email}</div>
              </div>
              <button onClick={(e) => handleDelete(e, c)} data-testid={`delete-client-${c.id}`} className="w-9 h-9 rounded-full flex items-center justify-center text-muted-foreground hover:text-red-400 hover:bg-red-500/10 flex-shrink-0"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <TypeBadge t={c.programme_type} />
              <PSBadge c={c} />
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
              <div><div className="label-mini">Weight</div><div className="text-xs mt-1 font-mono text-[#00C8FF]">{c.current_weight_kg ? `${c.current_weight_kg} kg` : '—'}</div></div>
              <div><div className="label-mini">Acct</div><div className="text-xs mt-1 font-medium">{c.status}</div></div>
              <div><div className="label-mini">Score</div><div className="text-xs mt-1 font-mono text-[var(--c-green-ink)]">{c.regen_score ?? 0}</div></div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="regen-card-elevated p-10 text-center text-muted-foreground text-sm">No clients found.</div>}
      </div>

      {/* DESKTOP: table */}
      <div className="hidden lg:block regen-card-elevated overflow-hidden">
        <div className="grid grid-cols-[2.4fr_1fr_1fr_0.9fr_1fr_1.5fr_1.1fr] px-5 py-3 label-mini border-b border-[var(--c-hair)]">
          <div>Client</div><div>Type</div><div>Plan</div><div>Weight</div><div>Status</div><div>Programme Status</div><div>Score</div>
        </div>
        {filtered.map(c => (
          <div key={c.id} onClick={() => nav(`/admin/clients/${c.id}`)} data-testid={`client-${c.id}`}
            className={`w-full grid grid-cols-[2.4fr_1fr_1fr_0.9fr_1fr_1.5fr_1.1fr] px-5 py-3 items-center hover:bg-[var(--c-film)] transition-all hover:translate-x-0.5 border-b border-[var(--c-hair)] last:border-0 cursor-pointer ${c.action_required ? 'bg-[#FF8A4C]/5' : ''}`}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#00C8FF] to-[var(--c-green)] flex items-center justify-center text-[#081726] font-bold text-xs">{(c.name || '?').split(' ').map(p => p[0]).slice(0,2).join('')}</div>
              <div className="text-left min-w-0"><div className="font-medium truncate">{c.name}</div><div className="text-xs text-muted-foreground truncate">{c.email}</div></div>
            </div>
            <div><TypeBadge t={c.programme_type} /></div>
            <div className="text-sm truncate">{c.program || '—'}</div>
            <div className="text-sm font-mono text-[#00C8FF]">{c.current_weight_kg ? `${c.current_weight_kg} kg` : '—'}</div>
            <div><span className={`pill ${c.status === 'active' ? 'bg-[var(--c-green-soft)] text-[var(--c-green-ink)]' : 'bg-[var(--c-film)] text-muted-foreground'}`}>{c.status}</span></div>
            <div><PSBadge c={c} /></div>
            <div className="flex items-center justify-between">
              <span><span className="font-mono text-lg text-[#00C8FF]">{c.regen_score}</span> <span className="text-xs text-muted-foreground">{c.level}</span></span>
              <button onClick={(e) => handleDelete(e, c)} data-testid={`delete-client-${c.id}`} className="w-8 h-8 rounded-full flex items-center justify-center text-muted-foreground hover:text-red-400 hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="p-10 text-center text-muted-foreground text-sm">No clients found.</div>}
      </div>
    </div>
  );
}
